<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import {
  USER_CONSTS as UC,
  LOOKUP_CONSTS as LC,
  FLIGHT_PHASE_CONSTS as FP,
  ACMF_REPORT_LIST_CONSTS as ARLC,
  ANALYTIC_COMPARISON_CONSTS as ACC,
  ANALYTIC_MODULE_CONSTS as AMC,
  CUSTOM_ALERTS_DERIVED_PARAMETERS_CONSTS as CA_DPC,
  AIRLINE_MANAGEMENT_CONSTS as AIRLINE_MC,
} from '@/stores/consts'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  data() {
    return {
      isLookupDataLoading: false,
      filterLookups: [],
    }
  },

  computed: {
    ...mapGetters(UC.NAMESPACE, [
      'mappedUserAirlines',
      'derivedParametersFullFlightPermission',
    ]),
    ...mapGetters(LC.NAMESPACE, [
      'mappedLuMasterModels',
      'mappedLuMasterModelsAll',
      'mappedLuTails',
      'mappedLuTailsAll',
      'mappedLuAnalytics',
      'mappedLuFlightPhases',
      'mappedLuFlights',
      'mappedLuDaysBack',
      'mappedLuSystemsByAirline',
      'mappedLuSystemsByAirlineAll',
      'mappedLuComponentsByAirline',
      'mappedLuComponentsByAirlineAll',
      'mappedLuParametersByAirline',
      'mappedUserModels',
      'mappedLuParametersByAirlineAll',
    ]),
    ...mapGetters(ARLC.NAMESPACE, [
      'mappedAcmfAirlines',
      'mappedAcmfAirlinesAll',
      'mappedAcmfMasterModels',
      'mappedAcmfMasterModelsAll',
      'mappedAcmfTails',
      'mappedAcmfTailsAll',
      'mappedAcmfSystems',
      'mappedAcmfSystemsAll',
      'mappedAcmfReportTypes',
      'mappedAcmfReportTypesAll',
      'mappedAcmfParameterNames',
    ]),
    ...mapGetters(ACC.NAMESPACE, [
      'mappedAccAnalytics',
      'mappedAccAnalyticsAll',
      'mappedAccRiskScores',
    ]),
    ...mapGetters(AMC.NAMESPACE, [
      'mappedAmcAnalyticModules',
      'mappedAmcAnalyticModulesAll',
      'mappedAmcFeatures',
    ]),

    ...mapState(CA_DPC.NAMESPACE, ['derivedParameters']),

    isDerivedParametersFlagActive() {
      return AppFeatureFlags.getDerivedParameters()
    },
  },

  methods: {
    ...mapActions(LC.NAMESPACE, [
      LC.GET_ANALYTICS,
      LC.GET_MASTER_MODELS,
      LC.GET_SYSTEMS,
      LC.GET_COMPONENTS,
      LC.GET_PARAMETERS,
      FP.GET_FLIGHT_PHASES,
    ]),
    ...mapActions(ARLC.NAMESPACE, [
      ARLC.GET_ACMF_AIRLINES,
      ARLC.GET_ACMF_MASTER_MODELS,
      ARLC.GET_ACMF_TAILS,
      ARLC.GET_ACMF_SYSTEMS,
      ARLC.GET_ACMF_REPORT_TYPES,
      ARLC.GET_ACMF_PARAMETER_NAMES,
    ]),
    ...mapActions(AMC.NAMESPACE, [
      AMC.GET_AMC_ANALYTIC_MODULES,
      AMC.GET_AMC_FEATURES,
    ]),
    ...mapActions(AIRLINE_MC.NAMESPACE,[
      AIRLINE_MC.GET_ALL_AGREEMENTS
    ]),

    ...mapActions(CA_DPC.NAMESPACE, [CA_DPC.GET_DERIVED_PARAMETERS]),

    async callFilterLookups(noGlobalFilter = false) {
      this.isLookupDataLoading = true

      if (this.isAirlineUser) {
        await Promise.all([
          this[LC.GET_MASTER_MODELS](),
          this[LC.GET_SYSTEMS](),
          this[LC.GET_COMPONENTS](),
          this[LC.GET_PARAMETERS](),
          this[LC.GET_ANALYTICS](),
          this[FP.GET_FLIGHT_PHASES](),
          this[AMC.GET_AMC_ANALYTIC_MODULES](),
          this[AMC.GET_AMC_FEATURES](),
        ])

        if (
          this.isDerivedParametersFlagActive &&
          this.derivedParametersFullFlightPermission
        ) {
          await this[CA_DPC.GET_DERIVED_PARAMETERS]()
        }
      }

      if (this.isAcmfUser) {
        await Promise.all([
          this[ARLC.GET_ACMF_AIRLINES](),
          this[ARLC.GET_ACMF_MASTER_MODELS](),
          this[ARLC.GET_ACMF_TAILS](),
          this[ARLC.GET_ACMF_SYSTEMS](),
          this[ARLC.GET_ACMF_REPORT_TYPES](),
          this[ARLC.GET_ACMF_PARAMETER_NAMES](),
          this[AIRLINE_MC.GET_ALL_AGREEMENTS](),
        ])
      }

      this.initFilterLookups(noGlobalFilter)

      this.isLookupDataLoading = false
    },

    async initFilterLookups(noGlobalFilter = false) {
      this.filterLookups = []
      await Promise.all([
        this.loadAirlines(),
        this.loadMasterModels(noGlobalFilter),
        this.loadTails(noGlobalFilter),
        this.loadSystems(noGlobalFilter),
        this.loadComponents(noGlobalFilter),
        this.loadParameters(noGlobalFilter),
        this.loadFlightPhases(),
        this.loadFlights(),
        this.loadDaysBack(),
        this.loadAcmfAirlines(noGlobalFilter),
        this.loadAcmfMasterModels(noGlobalFilter),
        this.loadAcmfTails(noGlobalFilter),
        this.loadAcmfSystems(noGlobalFilter),
        this.loadAcmfReportTypes(noGlobalFilter),
        this.loadAcmfParameterNames(),
        this.loadAmcAnalyticModules(noGlobalFilter),
        this.loadAmcFeatures(),
      ])
    },

    loadAirlines() {
      const mappedItem = {
        id: 'airline',
        items: this.mappedUserAirlines,
      }
      this.filterLookups.push(mappedItem)
    },

    loadMasterModels(noGlobalFilter) {
      // get masterModels and components
      const mms = noGlobalFilter
        ? this.mappedLuMasterModelsAll
        : this.mappedLuMasterModels
      const components = noGlobalFilter
        ? this.mappedLuComponentsByAirlineAll
        : this.mappedLuComponentsByAirline

      // only load masterModels that have valid components
      const filteredMasterModels = mms.filter(mm => {
        const masterModelId = mm.value
        const filteredComponents = components.filter(
          comp => comp.masterModelId === masterModelId,
        )
        return filteredComponents.length > 0
      })

      // add masterModel item to filterLookups
      const mappedItem = {
        id: 'masterModel',
        items: filteredMasterModels,
      }
      this.filterLookups.push(mappedItem)
    },

    loadTails(noGlobalFilter, filterTailsByAirlineId = null) {
      // get masterModels and systems
      let tails = noGlobalFilter ? this.mappedLuTailsAll : this.mappedLuTails

      if (filterTailsByAirlineId) {
        tails = tails.filter(tail => tail.airlineId === filterTailsByAirlineId)
      }

      const systems = noGlobalFilter
        ? this.mappedLuSystemsByAirlineAll
        : this.mappedLuSystemsByAirline

      // only load masterModels that have valid components
      const filteredTails = tails.filter(tail => {
        const masterModelId = tail.masterModelId
        const airlineId = tail.airlineId
        const filteredSystems = systems.filter(
          system =>
            system.masterModelId === masterModelId &&
            system.airlineId === airlineId,
        )
        return filteredSystems.length > 0
      })

      // if there is already a tails item in the filter lookups
      // remove it and replace it.  Otherwise, add it in
      const tailsFilterIndex = this.filterLookups.findIndex(
        lookup => lookup.id === 'tail',
      )

      // add tails item to filterLookups
      const mappedItem = {
        id: 'tail',
        items: filteredTails,
      }
      if (tailsFilterIndex === -1) {
        this.filterLookups.push(mappedItem)
      } else {
        this.filterLookups[tailsFilterIndex] = mappedItem
      }
    },

    loadSystems(noGlobalFilter) {
      const mappedItem = {
        id: 'system',
        items: noGlobalFilter
          ? this.mappedLuSystemsByAirlineAll
          : this.mappedLuSystemsByAirline,
      }

      if (this.derivedParameters.length) {
        // Add groupname to each system item
        mappedItem.items.map(i => (i.groupName = 'Systems'))
        // Add a new group for custom parameters

        const uniqueMasterModelId = [
          ...new Set(mappedItem.items.map(item => item.masterModelId)),
        ]

        uniqueMasterModelId.map(masterModelId => {
          mappedItem.items.unshift({
            systemId: 'custom_parameter',
            text: 'Custom Parameter',
            groupName: 'Custom',
            value: 'custom_parameter',
            masterModelId: masterModelId,
          })
        })
      }

      this.filterLookups.push(mappedItem)
    },

    loadComponents(noGlobalFilter) {
      const mappedItem = {
        id: 'component',
        items: noGlobalFilter
          ? this.mappedLuComponentsByAirlineAll
          : this.mappedLuComponentsByAirline,
      }

      if (this.derivedParameters.length) {
        // Add groupname to each component item
        mappedItem.items.map(i => (i.groupName = 'Components'))
        // Find unique master model ids so that csutom params will shown on selecting every system
        const uniqueMasterModelId = [
          ...new Set(mappedItem.items.map(item => item.masterModelId)),
        ]

        // Add a new group for custom parameters
        uniqueMasterModelId.map(() => {
          mappedItem.items.unshift({
            systemId: 'custom_parameter',
            componentId: 'custom_parameter',
            text: 'Custom Parameter',
            groupName: 'Custom',
            value: 'custom_parameter',
          })
        })
      }

      this.filterLookups.push(mappedItem)
    },

    loadParameters(noGlobalFilter) {
      const mappedItem = {
        id: 'parameter',
        items: noGlobalFilter
          ? this.mappedLuParametersByAirlineAll
          : this.mappedLuParametersByAirline,
      }

      if (this.derivedParameters.length) {
        // Add groupname to each paramter item
        mappedItem.items.map(i => (i.groupName = 'Parameters'))
        // Add a new group for custom parameters
        const derivedParametersList = []
        this.derivedParameters.forEach(
          ({ id, airlineId, masterModelId, name }) => {
            derivedParametersList.push({
              parameterId: id,
              airlineId,
              masterModelId,
              text: name,
              value: id,
              groupName: 'Custom',
              componentId: 'custom_parameter',
            })
          },
        )
        mappedItem.items.unshift(...derivedParametersList)
      }
      this.filterLookups.push(mappedItem)
    },

    loadFlightPhases() {
      const mappedItem = {
        id: 'flightPhase',
        items: this.mappedLuFlightPhases,
      }
      this.filterLookups.push(mappedItem)
    },

    loadFlights() {
      const mappedItem = {
        id: 'flight',
        items: this.mappedLuFlights,
      }
      this.filterLookups.push(mappedItem)
    },

    loadDaysBack() {
      const mappedItem = {
        id: 'daysBack',
        items: this.mappedLuDaysBack,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAcmfAirlines(noGlobalFilter) {
      const mappedItem = {
        id: 'acmfAirline',
        items: noGlobalFilter
          ? this.mappedAcmfAirlinesAll
          : this.mappedAcmfAirlines,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAcmfMasterModels(noGlobalFilter) {
      const mappedItem = {
        id: 'acmfMasterModel',
        items: noGlobalFilter
          ? this.mappedAcmfMasterModelsAll
          : this.mappedAcmfMasterModels,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAcmfTails(noGlobalFilter) {
      const mappedItem = {
        id: 'acmfTail',
        items: noGlobalFilter ? this.mappedAcmfTailsAll : this.mappedAcmfTails,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAcmfSystems(noGlobalFilter) {
      const mappedItem = {
        id: 'acmfSystem',
        items: noGlobalFilter
          ? this.mappedAcmfSystemsAll
          : this.mappedAcmfSystems,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAcmfReportTypes(noGlobalFilter) {
      const mappedItem = {
        id: 'acmfReportType',
        items: noGlobalFilter
          ? this.mappedAcmfReportTypesAll
          : this.mappedAcmfReportTypes,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAcmfParameterNames() {
      const mappedItem = {
        id: 'acmfParameterName',
        items: this.mappedAcmfParameterNames,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAmcAnalyticModules(noGlobalFilter) {
      const mappedItem = {
        id: 'amcAnalyticModule',
        items: noGlobalFilter
          ? this.mappedAmcAnalyticModulesAll
          : this.mappedAmcAnalyticModules,
      }
      this.filterLookups.push(mappedItem)
    },

    loadAmcFeatures() {
      const mappedItem = {
        id: 'amcFeature',
        items: this.mappedAmcFeatures,
      }
      this.filterLookups.push(mappedItem)
    },

    loadUserModels() {
      const mappedItem = {
        id: 'model',
        items: this.mappedUserModels,
      }
      this.filterLookups.push(mappedItem)
    },
  },
}
</script>
